/**
 * Check and prepare a Mediathek file for the poster PDF.
 *
 * Imports only `poster-pdf.js` (not the canvas-kit export index) so the pure
 * parts run under vitest without a DOM. Browser-only functions touch the DOM
 * inside their bodies only.
 */

import {
  loadPosterSourcePdf,
  PosterInputError,
  type PosterPdfSource,
  pdfPageGeometry,
} from "../../canvas-kit/export/poster-pdf.js";

/** iPad (iPadOS 17.7) canvas ceiling; nothing we decode into exceeds it. */
export const POSTER_MAX_PIXELS = 16_000_000;

/**
 * pdf-lib decodes and re-deflates a PNG synchronously on the main thread;
 * above this size that freezes the iPad, so bigger PNGs are re-encoded.
 */
export const POSTER_RAW_PNG_MAX_PX = 4_000_000;

const IMAGE_UNREADABLE_MESSAGE = "Bild konnte nicht gelesen werden.";

/** Format by magic bytes — the only thing pdf-lib's embedJpg/embedPng trust. */
export function sniffPosterImageFormat(bytes: Uint8Array): "jpg" | "png" | null {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return "jpg";
  }
  const png = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (bytes.length >= png.length && png.every((b, i) => bytes[i] === b)) return "png";
  return null;
}

/** Orientation tag present but unreadable — treat as rotated (re-encode). */
export const POSTER_ORIENTATION_UNKNOWN = 0;

/**
 * Walk the marker segments of a JPEG up to the start of scan. `visit` gets
 * each length-carrying segment's marker and data range `[dataStart, segEnd)`
 * and returns a result to stop with. Not a JPEG, a malformed or truncated
 * segment, or no hit → undefined.
 */
function scanJpegSegments<T>(
  bytes: Uint8Array,
  visit: (marker: number, dataStart: number, segEnd: number, view: DataView) => T | undefined,
): T | undefined {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return undefined;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let pos = 2;
  while (pos + 4 <= bytes.length) {
    if (bytes[pos] !== 0xff) return undefined;
    const marker = bytes[pos + 1] as number;
    if (marker === 0xff) {
      pos++; // fill byte
      continue;
    }
    // Start of scan / end of image: no metadata after this point.
    if (marker === 0xda || marker === 0xd9) return undefined;
    // Standalone markers (TEM, RSTn) carry no length.
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      pos += 2;
      continue;
    }
    const segLen = view.getUint16(pos + 2);
    const segEnd = pos + 2 + segLen;
    if (segLen < 2 || segEnd > bytes.length) return undefined;
    const hit = visit(marker, pos + 4, segEnd, view);
    if (hit !== undefined) return hit;
    pos = segEnd;
  }
  return undefined;
}

/**
 * EXIF orientation (tag 0x0112) of a JPEG, 1–8. No EXIF, not a JPEG, or a
 * truncated file → 1; a tag that is present but unreadable (wrong type or
 * count, value outside 1–8) → `POSTER_ORIENTATION_UNKNOWN`.
 */
export function readJpegOrientation(bytes: Uint8Array): number {
  return (
    scanJpegSegments(bytes, (marker, dataStart, segEnd, view) =>
      marker === 0xe1 && isExifHeader(bytes, dataStart, segEnd)
        ? exifOrientation(view, dataStart + 6, segEnd)
        : undefined,
    ) ?? 1
  );
}

/**
 * Frame size from the first SOFn header (C0–CF without DHT C4, JPG C8,
 * DAC CC) — the pixel size pdf-lib embeds, before any EXIF rotation.
 * No readable frame header → null.
 */
export function readJpegSofSize(bytes: Uint8Array): { w: number; h: number } | null {
  return (
    scanJpegSegments(bytes, (marker, dataStart, segEnd, view) => {
      if (marker < 0xc0 || marker > 0xcf || marker === 0xc4 || marker === 0xc8 || marker === 0xcc) {
        return undefined;
      }
      // precision (1), height (2), width (2)
      if (dataStart + 5 > segEnd) return null;
      const h = view.getUint16(dataStart + 1);
      const w = view.getUint16(dataStart + 3);
      return w > 0 && h > 0 ? { w, h } : null;
    }) ?? null
  );
}

function isExifHeader(bytes: Uint8Array, start: number, end: number): boolean {
  const exif = [0x45, 0x78, 0x69, 0x66, 0, 0]; // "Exif\0\0"
  return start + exif.length <= end && exif.every((b, i) => bytes[start + i] === b);
}

/** Orientation from IFD0 of the TIFF structure in `[tiff, end)`. */
function exifOrientation(view: DataView, tiff: number, end: number): number {
  if (tiff + 8 > end) return 1;
  const order = view.getUint16(tiff);
  if (order !== 0x4949 && order !== 0x4d4d) return 1;
  const le = order === 0x4949;
  if (view.getUint16(tiff + 2, le) !== 42) return 1;
  const ifd = tiff + view.getUint32(tiff + 4, le);
  if (ifd + 2 > end) return 1;
  const count = view.getUint16(ifd, le);
  for (let i = 0; i < count; i++) {
    const entry = ifd + 2 + i * 12;
    if (entry + 12 > end) return 1;
    if (view.getUint16(entry, le) !== 0x0112) continue;
    // SHORT, one value, stored left-aligned in the value field.
    if (view.getUint16(entry + 2, le) !== 3 || view.getUint32(entry + 4, le) !== 1) {
      return POSTER_ORIENTATION_UNKNOWN;
    }
    const o = view.getUint16(entry + 8, le);
    return o >= 1 && o <= 8 ? o : POSTER_ORIENTATION_UNKNOWN;
  }
  return 1;
}

export interface PosterImageStrategy {
  embed: "jpg" | "png" | "reencode";
  /** Pixel size of the bytes that end up in the PDF (upright). */
  outW: number;
  outH: number;
}

/**
 * How an image gets into the PDF. pdf-lib embeds JPEG/PNG raw but ignores
 * EXIF, so a JPEG stays raw only when it is certainly unrotated: orientation 1
 * and its frame size (`sof`) equal to what the browser shows — a mismatch means
 * the browser applied a rotation we couldn't read. PNGs above 4 MP (pdf-lib
 * decodes them synchronously) and anything pdf-lib can't read are redrawn at
 * ≤ 16 MP. Large unrotated JPEGs stay raw — pdf-lib never decodes them.
 * `pixelW/H` = displayed size, i.e. after EXIF rotation.
 */
export function posterImageStrategy(args: {
  mime: string;
  orientation: number;
  /** JPEG frame size from `readJpegSofSize`; missing → not kept raw. */
  sof?: { w: number; h: number } | null;
  pixelW: number;
  pixelH: number;
}): PosterImageStrategy {
  const { mime, orientation, sof, pixelW: w, pixelH: h } = args;
  if (
    (mime === "image/jpeg" || mime === "image/jpg") &&
    orientation === 1 &&
    sof?.w === w &&
    sof.h === h
  ) {
    return { embed: "jpg", outW: w, outH: h };
  }
  if (mime === "image/png" && w * h <= POSTER_RAW_PNG_MAX_PX) {
    return { embed: "png", outW: w, outH: h };
  }
  const s = Math.min(1, Math.sqrt(POSTER_MAX_PIXELS / (w * h)));
  return { embed: "reencode", outW: Math.floor(w * s), outH: Math.floor(h * s) };
}

/** Page sizes as a viewer shows them (CropBox + `/Rotate`), in pt. */
export async function inspectPdfForPoster(
  bytes: Uint8Array,
): Promise<{ pages: Array<{ w: number; h: number }> }> {
  const doc = await loadPosterSourcePdf(bytes);
  const pages = doc.getPages();
  if (pages.length === 0) throw new PosterInputError("Die PDF enthält keine Seiten.");
  return { pages: pages.map((page) => pdfPageGeometry(page).display) };
}

export interface ImagePosterInfo {
  /**
   * Upright decoded size. Plan the poster (aspect, dpi) from
   * `strategy.outW/outH` — the pixels that actually end up in the PDF.
   */
  pixelW: number;
  pixelH: number;
  strategy: PosterImageStrategy;
  /** Object URL of the file; the caller revokes it. */
  previewUrl: string;
}

/** Decode an image from an object URL; browsers apply EXIF orientation. */
async function decodeImage(url: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.src = url;
  let ok = false;
  try {
    await img.decode();
    // An SVG without intrinsic size decodes to 0 × 0 — no usable aspect.
    ok = img.naturalWidth > 0 && img.naturalHeight > 0;
  } catch {
    ok = false;
  }
  if (!ok) {
    img.removeAttribute("src");
    throw new PosterInputError(IMAGE_UNREADABLE_MESSAGE);
  }
  return img;
}

const SNIFFED_MIME = { jpg: "image/jpeg", png: "image/png" } as const;

/** Browser only. */
export async function inspectImageForPoster(
  bytes: Uint8Array,
  mime: string,
): Promise<ImagePosterInfo> {
  const previewUrl = URL.createObjectURL(new Blob([bytes as BlobPart], { type: mime }));
  try {
    const img = await decodeImage(previewUrl);
    const pixelW = img.naturalWidth;
    const pixelH = img.naturalHeight;
    img.removeAttribute("src");
    // Trust the bytes, not the declared type: only real JPEG/PNG go in raw.
    const format = sniffPosterImageFormat(bytes);
    const strategy = posterImageStrategy({
      mime: format ? SNIFFED_MIME[format] : "application/octet-stream",
      orientation: format === "jpg" ? readJpegOrientation(bytes) : 1,
      sof: format === "jpg" ? readJpegSofSize(bytes) : null,
      pixelW,
      pixelH,
    });
    return { pixelW, pixelH, strategy, previewUrl };
  } catch (err) {
    URL.revokeObjectURL(previewUrl);
    throw err;
  }
}

/**
 * Bytes for `buildPosterPdf`. Browser only for `reencode`: the image is drawn
 * upright onto one `outW × outH` canvas (≤ 16 MP) and saved as JPEG.
 */
export async function prepareImagePosterSource(
  bytes: Uint8Array,
  mime: string,
  strategy: PosterImageStrategy,
): Promise<PosterPdfSource> {
  if (strategy.embed !== "reencode" && sniffPosterImageFormat(bytes) === strategy.embed) {
    return { kind: strategy.embed, bytes };
  }
  const { outW, outH } = strategy;
  if (!(outW > 0 && outH > 0 && outW * outH <= POSTER_MAX_PIXELS)) {
    throw new PosterInputError(IMAGE_UNREADABLE_MESSAGE);
  }
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: mime }));
  let img: HTMLImageElement | null = null;
  const canvas = document.createElement("canvas");
  try {
    img = await decodeImage(url);
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new PosterInputError(IMAGE_UNREADABLE_MESSAGE);
    // JPEG has no alpha; transparent areas print white like on paper.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, outW, outH);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, outW, outH);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.92),
    );
    if (!blob) throw new PosterInputError(IMAGE_UNREADABLE_MESSAGE);
    canvas.width = 0;
    canvas.height = 0;
    return { kind: "jpg", bytes: new Uint8Array(await blob.arrayBuffer()) };
  } finally {
    canvas.width = 0;
    canvas.height = 0;
    img?.removeAttribute("src");
    URL.revokeObjectURL(url);
  }
}
